import { IDynamicPerson } from "@microsoft/mgt-react";
import { graphConfig } from "../authConfig";
import { GraphUser } from "../objects/graph-user";
import * as authConfig from "../authConfig";

/**
 * Microsoft Graph API Service
 * 
 * This module provides utility functions for interacting with Microsoft Graph API
 * to retrieve user information, group data, and perform people searches.
 * All functions require a valid access token for authentication.
 * 
 * @author Your Team
 * @version 1.0.0
 */

/**
 * Gets the current user profile information from Microsoft Graph
 * 
 * Makes a GET request to the /me endpoint to retrieve the authenticated user's profile.
 * This includes basic information like display name, email, job title, and other
 * profile attributes available in Azure AD.
 * 
 * @param accessToken - Valid Bearer token for Microsoft Graph authentication
 * @returns Promise<any> - User profile object containing displayName, mail, jobTitle, etc.
 * 
 * @example
 * ```typescript
 * const userProfile = await getUserProfileInformation(accessToken);
 * console.log(userProfile.displayName); // "John Doe"
 * console.log(userProfile.mail); // "john.doe@company.com"
 * ```
 * 
 * @throws Will log error to console if request fails
 * @see https://docs.microsoft.com/en-us/graph/api/user-get
 */
export async function getUserProfileInformation(accessToken: string): Promise<any> {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    const options = {
        method: "GET",
        headers: headers
    };

    return fetch(graphConfig.graphMeEndpoint, options)
        .then(response => response.json())
        .catch(error => console.log(error));
}

/**
 * Retrieves user information by email address
 * 
 * Fetches user details using their email address. Supports both regular users
 * and guest users with different query approaches:
 * - Regular users: Direct lookup using email as user principal name
 * - Guest users: Filter-based search since guests may have different UPN format
 * 
 * @param accessToken - Valid Bearer token for Microsoft Graph authentication
 * @param userEmail - Email address of the user to retrieve
 * @param isGuest - Optional flag indicating if the user is a guest (default: false)
 * @returns Promise<any> - User object or array of users (for guest search)
 * 
 * @example
 * ```typescript
 * // Get regular user
 * const user = await getUserByEmail(accessToken, "john@company.com");
 * 
 * // Get guest user
 * const guestUsers = await getUserByEmail(accessToken, "external@partner.com", true);
 * ```
 * 
 * @throws Will log error to console if request fails
 * @see https://docs.microsoft.com/en-us/graph/api/user-get
 * @see https://docs.microsoft.com/en-us/graph/api/user-list
 */
export async function getUserByEmail(
  accessToken: string,
  userEmail: string,
  isGuest: boolean = false
): Promise<any> {
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append("Authorization", bearer);

  const options = {
    method: "GET",
    headers: headers,
  };
  
  if (isGuest) {
    // For guest users, use filter query as their UPN might be different from email
    return fetch(
      `https://graph.microsoft.com/v1.0/users/?$filter=mail eq '${userEmail}'`,
      options
    )
      .then((response) => response.json())
      .catch((error) => console.log(error));
  } else {
    // For regular users, direct lookup by email (assuming email = UPN)
    return fetch(`https://graph.microsoft.com/v1.0/users/${userEmail}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

/**
 * Retrieves all Microsoft 365 groups owned by the current authenticated user
 * 
 * Makes a request to get all M365 groups where the current user is listed as an owner.
 * This includes Microsoft Teams, SharePoint sites, and other group-connected services.
 * 
 * @param accessToken - Valid Bearer token for Microsoft Graph authentication
 * @returns Promise<any> - Array of group objects owned by the current user
 * 
 * @example
 * ```typescript
 * const ownedGroups = await getOwnedM365Resources(accessToken);
 * ownedGroups.value.forEach(group => {
 *   console.log(`Group: ${group.displayName} (${group.id})`);
 * });
 * ```
 * 
 * @throws Will log error to console if request fails
 * @see https://docs.microsoft.com/en-us/graph/api/user-list-ownedgroups
 */
export async function getOwnedM365Resources(accessToken: string): Promise<any> {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    const options = {
        method: "GET",
        headers: headers
    };

    return fetch(graphConfig.graphOwnedGroupsEndpoint, options)
        .then(response => response.json())
        .catch(error => console.log(error));
}

/**
 * Searches for people across the organization by display name, job title, or email
 * 
 * Performs a fuzzy search across multiple user attributes using Microsoft Graph's
 * advanced search capabilities. The search supports partial matching and will find
 * users whose displayName, jobTitle, or mail fields contain the search text.
 * Results are limited to 10 items for optimal performance.
 * 
 * @param accessToken - Valid Bearer token for Microsoft Graph authentication
 * @param searchText - Text to search for across user attributes (case-insensitive)
 * @returns Promise<any> - Array of matching user objects (max 10 results)
 * 
 * @example
 * ```typescript
 * // Search by partial name
 * const results = await searchPeople(accessToken, "John");
 * 
 * // Search by job title
 * const managers = await searchPeople(accessToken, "Manager");
 * 
 * // Search by email domain
 * const users = await searchPeople(accessToken, "@company.com");
 * ```
 * 
 * @throws Will throw error if accessToken or searchText are missing
 * @throws Will throw error if HTTP request fails
 * @see https://docs.microsoft.com/en-us/graph/search-query-parameter
 * 
 * @note Requires ConsistencyLevel: eventual header for advanced search queries
 * @note Search results may have slight delays due to eventual consistency model
 */
export async function searchPeople(
  accessToken: string,
  searchText: string
): Promise<any> {
  if (!searchText || !accessToken) {
    throw new Error('Access token and search text are required');
  }

  const headers = new Headers();
  headers.append("Authorization", `Bearer ${accessToken}`);
  headers.append("ConsistencyLevel", "eventual"); // Required for $search queries

  const options = {
    method: "GET",
    headers: headers,
  };

  // Encode the search text to handle special characters
  const encodedSearchText = encodeURIComponent(searchText);
  
  // Use $search parameter to search across multiple fields
  // $top=10 limits results to 10 items for performance
  const searchUrl = `https://graph.microsoft.com/v1.0/users?$search="displayName:${encodedSearchText}" OR "jobTitle:${encodedSearchText}" OR "mail:${encodedSearchText}"&$top=10&$select=id,displayName,mail,jobTitle,userPrincipalName`;

  try {
    const response = await fetch(searchUrl, options);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.value || [];
  } catch (error) {
    console.error('Error searching people:', error);
    throw error;
  }
}

/**
 * Retrieves the owners of a specific Microsoft 365 group
 * 
 * Fetches all users who are designated as owners of the specified group.
 * Maps the response to GraphUser objects with the custom extension attribute
 * stored in the additionalData property for compatibility with ownership checks.
 * 
 * @param accessToken - Valid Bearer token for Microsoft Graph authentication
 * @param groupId - The unique identifier (GUID) of the Microsoft 365 group
 * @returns Promise<GraphUser[]> - Array of GraphUser objects representing group owners
 * 
 * @example
 * ```typescript
 * const owners = await getGroupOwners(accessToken, "12345678-1234-1234-1234-123456789012");
 * 
 * owners.forEach(owner => {
 *   console.log(`Owner: ${owner.displayName}`);
 *   console.log(`Email: ${owner.mail}`);
 *   console.log(`Country: ${owner.country || 'Not specified'}`);
 *   
 *   // Access badge type from additionalData using config
 *   const badgeType = owner.additionalData?.[otherValues.badgeTypeAttributeName];
 *   console.log(`Badge Type: ${badgeType}`);
 *   
 *   // Check ownership status
 *   const isValidOwner = checkOwnershipStatus(owner);
 *   console.log(`Valid Owner: ${isValidOwner}`);
 * });
 * ```
 * 
 * @throws Will throw error if accessToken or groupId are missing
 * @throws Will throw error if HTTP request fails or group doesn't exist
 * @see https://docs.microsoft.com/en-us/graph/api/group-list-owners
 * 
 * @note The extension attribute is dynamically loaded from otherValues.badgeTypeAttributeName
 *       and mapped to additionalData for compatibility with checkOwnershipStatus function
 * @note Country field can be null for some users (especially service accounts)
 * @note Mail field may contain GUID-based addresses for certain account types
 * @note GraphUser objects have loaded property set to true when successfully populated
 */
export async function getGroupOwners(
  accessToken: string,
  groupId: string
): Promise<GraphUser[]> {
  if (!groupId || !accessToken) {
    throw new Error('Access token and group ID are required');
  }

  const headers = new Headers();
  headers.append("Authorization", `Bearer ${accessToken}`);

  const options = {
    method: "GET",
    headers: headers,
  };

  // Select required attributes including the dynamic extension attribute
  const selectFields = `id,displayName,givenName,surname,userPrincipalName,mail,jobTitle,businessPhones,mobilePhone,officeLocation,preferredLanguage,country,${authConfig.otherValues.badgeTypeAttributeName}`;
  const ownersUrl = `https://graph.microsoft.com/v1.0/groups/${groupId}/owners?$select=${selectFields}`;

  try {
    const response = await fetch(ownersUrl, options);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const users = data.value || [];

    // Map Graph API response to GraphUser objects
    return users.map((user: any) => {
      const graphUser = new GraphUser();
      
      // Map standard properties
      graphUser["@odata.context"] = data["@odata.context"] || "";
      graphUser.id = user.id || "";
      graphUser.displayName = user.displayName || "";
      graphUser.givenName = user.givenName || "";
      graphUser.surname = user.surname || "";
      graphUser.userPrincipalName = user.userPrincipalName || "";
      graphUser.mail = user.mail || "";
      graphUser.jobTitle = user.jobTitle || "";
      graphUser.businessPhones = user.businessPhones || [];
      graphUser.mobilePhone = user.mobilePhone || null;
      graphUser.officeLocation = user.officeLocation || null;
      graphUser.preferredLanguage = user.preferredLanguage || null;
      graphUser.country = user.country || "";
      
      // Map extension attribute to additionalData using dynamic attribute name
      graphUser.additionalData = {
        [authConfig.otherValues.badgeTypeAttributeName]: 
          user[authConfig.otherValues.badgeTypeAttributeName] || ""
      };
      
      // Mark as loaded
      graphUser.loaded = true;
      
      return graphUser;
    });

  } catch (error) {
    console.error('Error fetching group owners:', error);
    throw error;
  }
}